import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  Grid,
  Link,
  Step,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
} from "@mui/material";
import { makeStyles, styled } from "@mui/styles";
import { Formik } from "formik";
import FormikErrorFocus from "formik-error-focus";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { LoadingButton } from "@mui/lab";
import { CheckCircle, InfoRounded, ArrowBack, ArrowForward } from "@mui/icons-material";

import axios from "../utils/axios";
import Autocomplete from "../components/Autocomplete";
import Checkbox from "../components/Checkbox";
import TextField from "../components/TextField";
import DatePicker from "../components/DatePicker";
import PhoneField from "../components/PhoneField";
import GoogleSignInButton from "../components/GoogleSignInButton";
import { BACKDROP_COLOR } from "../styles/theme";
import { sleep } from "../utils/helpers";
import {
  INSTITUTION,
  PHONE_REG_EXP,
  COUNTRIES,
  YEARS,
  UNIVERSITY_YEARS,
} from "../utils/consts";

const useStyles = makeStyles(() => ({
  card: {
    padding: 20,
    background: "#fff",
  },
  icon: {
    marginRight: 12,
  },
  tooltip: {
    padding: 16,
    fontSize: 13,
  },
}));

const cardPadding = { padding: [0, 2, 4] };

const steps = ["Account Type", "Institution", "Personal Details"];

const initialValues = {
  // Step 1
  country: null,
  region: null,
  // Step 2
  institution: null,
  teams: [],
  secondary_year: null,
  teacher_request: false,
  // Step 3
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  password: "",
  password_confirmation: "",
  dob: null,
  agree: false,
  subscribe: false,
  // Google auth data
  isGoogleAuth: false,
};

// Validation schema for each step
const getValidationSchema = (step, isGoogleAuth) => {
  if (step === 0) {
    // Step 1: Country/Region
    return Yup.object().shape({
      country: Yup.object()
        .required("Please select a country")
        .nullable(),
      region: Yup.object().nullable().when("country", {
        is: (country) => !!country?.name,
        then: (schema) => schema.required("Please select a location"),
      }),
    });
  }

  if (step === 1) {
    // Step 2: Institution
    return Yup.object().shape({
      institution: Yup.object()
        .required(`Please choose a ${INSTITUTION}`)
        .shape({ id: Yup.number().required(`Please choose a ${INSTITUTION}`) })
        .nullable(),
      teams: Yup.array().min(1, "At least one team is required"),
      secondary_year: Yup.object().nullable(),
      teacher_request: Yup.boolean().when(["institution", "secondary_year"], {
        is: (institution, secondaryYear) => {
          if (
            (institution?.type === "secondary" || institution?.type === "intermediate") &&
            !secondaryYear?.value
          ) {
            return true;
          }
          return false;
        },
        then: (schema) =>
          schema
            .required("Please confirm if you are a teacher")
            .oneOf(
              [true],
              "Please select a school year or confirm that you are a teacher"
            ),
      }),
    });
  }

  if (step === 2) {
    // Step 3: Personal Details
    const baseSchema = {
      first_name: Yup.string()
        .min(1, "Too Short!")
        .max(50, "Too Long!")
        .required("Required"),
      last_name: Yup.string()
        .min(1, "Too Short!")
        .max(50, "Too Long!")
        .required("Required"),
      email: Yup.string().email("Invalid email").required("Required"),
      phone: Yup.string()
        .matches(PHONE_REG_EXP, "Phone number is not valid")
        .required("Required"),
      dob: Yup.date()
        .nullable()
        .typeError("Invalid Date")
        .required("Please enter a date of birth")
        .max(
          dayjs().subtract(5, "y").format("YYYY-MM-DD"),
          "You must be over five"
        )
        .min(
          dayjs().subtract(100, "y").format("YYYY-MM-DD"),
          "Please review date of birth"
        ),
      agree: Yup.boolean()
        .required("The privacy policy must be accepted.")
        .oneOf([true], "The privacy policy must be accepted."),
    };

    // Add password fields only for manual signup
    if (!isGoogleAuth) {
      baseSchema.password = Yup.string().min(8, "Too Short!").required("Required");
      baseSchema.password_confirmation = Yup.mixed()
        .oneOf([null, Yup.string()])
        .when("password", {
          is: (value) => value?.length >= 8,
          then: (schema) =>
            schema
              .concat(Yup.string())
              .oneOf([Yup.ref("password")], "Passwords must match")
              .required("Confirm Password is required"),
        });
    }

    return Yup.object().shape(baseSchema);
  }

  return Yup.object().shape({});
};

export default function SignUp({ open, onClose, initialGoogleData }) {
  const classes = useStyles();
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const [countriesOptions, setCountriesOptions] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [institutionsCache, setInstitutionsCache] = useState({});
  const [currentInstitutions, setCurrentInstitutions] = useState([]);
  const [loadingInstitutions, setLoadingInstitutions] = useState(false);
  const [errors, setErrors] = useState([]);

  // Fetch countries from API on mount
  useEffect(() => {
    axios.get("countries").then((res) => {
      setCountriesOptions(res?.data?.data || []);
    }).catch(() => {});
  }, []);

  // Get cache key based on country and region/location
  const getCacheKey = useCallback((country, region) => {
    if (!country?.id || !region?.id) return null;
    return `${country.id}-${region.id}`;
  }, []);
  const [creating, setCreating] = useState(false);
  const [viewTooltip, setViewTooltip] = useState(false);
  const [createdAccount, setCreated] = useState(false);
  // Initialize with data passed from SignIn (when user clicks Google from SignIn)
  const [googleUserData, setGoogleUserData] = useState(initialGoogleData || null);

  // Prevent closing dialog when Google auth is active but profile incomplete
  const handleDialogClose = useCallback(() => {
    if (googleUserData?.isGoogleAuth) {
      // Don't allow closing - user must complete profile
      return;
    }
    onClose();
  }, [googleUserData, onClose]);

  const handleToogleTooltip = useCallback((event) => {
    event.preventDefault();
    setViewTooltip((prev) => !prev);
  }, []);

  const handleGoogleSuccess = useCallback(
    ({ requiresProfileCompletion, user }) => {
      if (requiresProfileCompletion && user) {
        // Store Google user data - user still needs to select country/region
        setGoogleUserData({
          first_name: user.first_name || "",
          last_name: user.last_name || "",
          email: user.email || "",
          isGoogleAuth: true,
        });
        // Stay on Step 0 to select country/region, but Google is authenticated
      } else {
        // Full profile exists, close signup and navigate home
        onClose();
        navigate("/");
      }
    },
    [navigate, onClose]
  );

  // Fetch locations for a country
  const fetchLocations = useCallback(async (country) => {
    if (!country?.id) return;
    setLoadingLocations(true);
    try {
      const response = await axios.get(`locations?filter[country_id]=${country.id}`);
      setLocations(response?.data?.data || []);
    } finally {
      setLoadingLocations(false);
    }
  }, []);

  // Fetch all institutions for country/location (with caching)
  const fetchAllInstitutions = useCallback(async (country, region) => {
    const cacheKey = getCacheKey(country, region);
    if (!cacheKey) return;

    // Check if already cached
    if (institutionsCache[cacheKey]) {
      setCurrentInstitutions(institutionsCache[cacheKey]);
      return;
    }

    setLoadingInstitutions(true);
    try {
      let url = `institutions?include=teams&limit=9999&filter[location_id]=${region.id}`;

      const response = await axios.get(url);
      const data = response?.data?.data || [];

      // Store in cache
      setInstitutionsCache(prev => ({ ...prev, [cacheKey]: data }));
      setCurrentInstitutions(data);
    } finally {
      setLoadingInstitutions(false);
    }
  }, [getCacheKey, institutionsCache]);

  // Get current cached institutions for the selected country/region
  const getCachedInstitutions = useCallback((country, region) => {
    const cacheKey = getCacheKey(country, region);
    return cacheKey ? (institutionsCache[cacheKey] || []) : [];
  }, [getCacheKey, institutionsCache]);

  // Client-side filtering of institutions
  const handleInstitutionSearchChange = useCallback((text, country, region) => {
    const cached = getCachedInstitutions(country, region);
    if (!text) {
      setCurrentInstitutions(cached);
      return;
    }
    const filtered = cached.filter((inst) =>
      inst.name.toLowerCase().includes(text.toLowerCase())
    );
    setCurrentInstitutions(filtered);
  }, [getCachedInstitutions]);

  // Trigger fetch when country changes
  const handleCountryChange = useCallback((country, setFieldValue) => {
    // Reset region/location when country changes
    setFieldValue("region", null);
    setLocations([]);
    setCurrentInstitutions([]);

    if (country?.id) {
      fetchLocations(country);
    }
  }, [fetchLocations]);

  // Trigger fetch when location changes (for non-NZ countries)
  const handleLocationChange = useCallback((location, country) => {
    if (location?.id) {
      fetchAllInstitutions(country, location);
    }
  }, [fetchAllInstitutions]);

  const handleNext = useCallback(
    async (values, validateForm, setTouched, setFieldTouched) => {
      // Validate current step
      const errors = await validateForm();
      const stepFields = getStepFields(activeStep);
      const hasErrors = stepFields.some((field) => errors[field]);

      if (hasErrors) {
        // Touch all fields in current step to show errors
        stepFields.forEach((field) => setFieldTouched(field, true));
        return;
      }

      // If moving to Step 1 (institution), ensure institutions are loaded
      if (activeStep === 0) {
        const cacheKey = getCacheKey(values.country, values.region);
        if (!cacheKey || !institutionsCache[cacheKey]) {
          // Not cached yet, fetch now
          await fetchAllInstitutions(values.country, values.region);
        } else {
          // Already cached, just set current institutions
          setCurrentInstitutions(institutionsCache[cacheKey]);
        }
      }

      setActiveStep((prev) => prev + 1);
    },
    [activeStep, fetchAllInstitutions, getCacheKey, institutionsCache]
  );

  const handleBack = useCallback(() => {
    setActiveStep((prev) => prev - 1);
  }, []);

  const getStepFields = (step) => {
    if (step === 0) return ["country", "region"];
    if (step === 1) return ["institution", "teams", "secondary_year", "teacher_request"];
    if (step === 2)
      return [
        "first_name",
        "last_name",
        "email",
        "phone",
        "dob",
        "password",
        "password_confirmation",
        "agree",
      ];
    return [];
  };

  const handleSubmitFormik = useCallback(
    async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);
        setErrors([]);

        if (googleUserData?.isGoogleAuth) {
          // Google auth flow - update existing user profile
          const payload = {
            phone: values.phone,
            country: values.country?.name,
            region: values.region?.title || values.region?.name || null,
            teacher_request: values.teacher_request ? dayjs() : null,
            volunteer_details: {
              dob: dayjs(values.dob).format("YYYY-MM-DD"),
              institution_id: values.institution?.id,
              secondary_year: values.secondary_year?.value || null,
            },
            team_ids: values.teams.map((o) => ({ id: o.id })),
          };

          // Get current user ID from profile endpoint
          const profileResponse = await axios.get("auth/profile", { withCredentials: true });
          const userId = profileResponse.data.data.id;

          await axios.put(`users/${userId}?sync[team_ids]=true`, payload);

          // Refresh profile
          await axios.get("auth/profile", { withCredentials: true });

          setCreating(true);
          await sleep(2000);
          setCreated(true);
          await sleep(3000);

          onClose();
          navigate("/");
        } else {
          // Manual signup flow
          const obj = {
            ...values,
            country: values.country?.name || null,
            region: values.region?.title || values.region?.name || null,
            year_level_type: values.year_level_type?.value || null,
            year_level: values.secondary_year?.value || null,
            volunteer_details: {
              dob: dayjs(values.dob).toISOString(),
              institution_id: values.institution.id,
              has_graduated: !values.secondary_year?.value,
              secondary_year: values.secondary_year?.value ?? null,
            },
            team_ids: values.teams.map((t) => t.id),
          };

          await axios.post("auth/register", obj);
          setCreating(true);
          await sleep(3000);
          setCreated(true);
          await sleep(5000);
          navigate("/");
          setCreating(false);
          setCreated(false);
          onClose();
        }
      } catch (error) {
        setErrors(
          Object.values(
            error.errors || error.response?.data?.errors || { empty: "Unknown error please contact SVA" }
          ).flat()
        );
      } finally {
        setSubmitting(false);
      }
    },
    [navigate, onClose, googleUserData]
  );

  const creatingMemo = useMemo(
    () =>
      !createdAccount ? (
        <Backdrop
          sx={{
            backgroundColor: "rgba(240, 242, 250, 0.93)",
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
          open
        >
          <Box
            alignItems="center"
            display="flex"
            justifyContent="center"
            flexDirection="column"
          >
            <Typography
              align="center"
              sx={{
                color: "primary.main",
                fontSize: 32,
                fontWeight: 800,
                lineHeight: "44px",
                maxWidth: 440,
              }}
            >
              Hold on tight.. we are setting up your account!
            </Typography>
            <CircularProgress sx={{ marginTop: 3 }} color="inherit" />
          </Box>
        </Backdrop>
      ) : (
        <Backdrop
          sx={{
            backgroundColor: "rgba(240, 242, 250, 0.93)",
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
          open
        >
          <Box
            alignItems="center"
            display="flex"
            justifyContent="center"
            flexDirection="column"
          >
            <Typography
              align="center"
              sx={{
                color: "primary.main",
                fontSize: 32,
                fontWeight: 800,
                lineHeight: "44px",
                maxWidth: 440,
              }}
            >
              Account created!
            </Typography>
            {!googleUserData?.isGoogleAuth && (
              <Typography
                align="center"
                sx={{
                  color: "primary.main",
                  fontSize: 24,
                  fontWeight: 700,
                  lineHeight: "44px",
                  maxWidth: 440,
                }}
              >
                Please verify your email account to sign in.
              </Typography>
            )}
            <CheckCircle
              sx={{ fontSize: 48, marginTop: 3, color: "#00A59B" }}
              color="inherit"
            />
          </Box>
        </Backdrop>
      ),
    [createdAccount, googleUserData]
  );

  // Merge initial values with Google data if available
  const formInitialValues = useMemo(() => {
    if (googleUserData) {
      return {
        ...initialValues,
        first_name: googleUserData.first_name,
        last_name: googleUserData.last_name,
        email: googleUserData.email,
        isGoogleAuth: true,
      };
    }
    return initialValues;
  }, [googleUserData]);

  if (!open) {
    return null;
  }

  return !creating ? (
    <Dialog
      BackdropComponent={styled(Backdrop, {
        name: "MuiModal",
        slot: "Backdrop",
        overridesResolver: (props, styles) => styles.backdrop,
      })({ zIndex: -1, backgroundColor: BACKDROP_COLOR })}
      open={open}
      onClose={handleDialogClose}
      fullWidth
      maxWidth="md"
      scroll="body"
      transitionDuration={{ appear: 0 }}
      disableEscapeKeyDown={googleUserData?.isGoogleAuth}
    >
      <Card className={classes.card} sx={cardPadding}>
        <CardContent>
          {/* Stepper */}
          <Box sx={{ mb: 4 }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          <Formik
            initialValues={formInitialValues}
            validationSchema={() => getValidationSchema(activeStep, googleUserData?.isGoogleAuth)}
            onSubmit={handleSubmitFormik}
            enableReinitialize
          >
            {({
              handleSubmit,
              values,
              isSubmitting,
              setFieldValue,
              validateForm,
              setTouched,
              setFieldTouched,
            }) => (
              <form onSubmit={handleSubmit} className={classes.form}>
                {/* Step 1: Account Type / Country Selection */}
                {activeStep === 0 && (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography
                        align="center"
                        sx={{ fontSize: 24, fontWeight: 700, color: "primary.main", mb: 1 }}
                      >
                        Create your SVA account
                      </Typography>
                      <Typography align="center" sx={{ color: "text.secondary", mb: 3 }}>
                        {googleUserData?.isGoogleAuth
                          ? "Great! Now tell us where you're from."
                          : "Choose how you'd like to sign up."}
                      </Typography>
                    </Grid>

                    {/* Show Google button only if not authenticated */}
                    {!googleUserData?.isGoogleAuth ? (
                      <>
                        <Grid item xs={12}>
                          <GoogleSignInButton onSuccess={handleGoogleSuccess} />
                        </Grid>

                        <Grid item xs={12}>
                          <Box display="flex" alignItems="center" my={2}>
                            <Box flexGrow={1} height="1px" bgcolor="divider" />
                            <Typography sx={{ px: 2, color: "text.secondary" }}>Or</Typography>
                            <Box flexGrow={1} height="1px" bgcolor="divider" />
                          </Box>
                        </Grid>
                      </>
                    ) : (
                      <Grid item xs={12}>
                        <Alert severity="success" sx={{ mb: 1 }}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <CheckCircle fontSize="small" />
                            <Typography>
                              Signed in as <strong>{googleUserData.email}</strong>
                            </Typography>
                          </Box>
                        </Alert>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          <Typography variant="body2">
                            Please complete all steps to finish setting up your account.
                          </Typography>
                        </Alert>
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <Autocomplete
                        required
                        options={countriesOptions}
                        name="country"
                        label="Which country are you in?"
                        onChange={(country) => handleCountryChange(country, setFieldValue)}
                      />
                    </Grid>

                    {values.country && (
                      <Grid item xs={12}>
                        <Autocomplete
                          required
                          loading={loadingLocations}
                          options={locations}
                          name="region"
                          label="Which region are you in?"
                          getOptionLabel={(option) => option?.title || option?.name || ""}
                          onChange={(location) => handleLocationChange(location, values.country)}
                        />
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <LoadingButton
                        fullWidth
                        variant="contained"
                        loading={loadingInstitutions || loadingLocations}
                        onClick={() =>
                          handleNext(values, validateForm, setTouched, setFieldTouched)
                        }
                        endIcon={<ArrowForward />}
                        disabled={!values.country || !values.region}
                        sx={{ mt: 2 }}
                      >
                        Continue
                      </LoadingButton>
                    </Grid>
                  </Grid>
                )}

                {/* Step 2: Institution Selection */}
                {activeStep === 1 && (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography
                        align="center"
                        sx={{ fontSize: 24, fontWeight: 700, color: "primary.main", mb: 1 }}
                      >
                        Institution Selection
                      </Typography>
                      <Typography align="center" sx={{ color: "text.secondary", mb: 2 }}>
                        Connect user to the correct programme and dashboard
                      </Typography>
                    </Grid>

                    {/* Show selected country/region as chips */}
                    <Grid item xs={12}>
                      <Box display="flex" gap={1} mb={2}>
                        <Typography variant="body2" sx={{ mr: 1 }}>
                          Country & Region you&apos;re from:
                        </Typography>
                        {values.country && (
                          <Chip
                            label={values.country.name}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {values.region && (
                          <Chip
                            label={values.region.title || values.region.name}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Autocomplete
                        required
                        loading={loadingInstitutions}
                        options={currentInstitutions}
                        handleInputChange={(text) => handleInstitutionSearchChange(text, values.country, values.region)}
                        name="institution"
                        label={`What ${INSTITUTION.toLowerCase()} are you part of?`}
                      />
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        If you are not a part of any {INSTITUTION}, please sign up to{" "}
                        <strong>SVA General</strong>
                      </Typography>
                    </Grid>

                    {values?.institution && (
                      <>
                        {/* Year Level */}
                        <Grid item xs={12}>
                          {(values?.institution?.type === "secondary" ||
                            values?.institution?.type === "intermediate") &&
                            !values?.teacher_request && (
                              <Autocomplete
                                required
                                options={YEARS}
                                label="What year are you in at school?"
                                name="secondary_year"
                              />
                            )}
                          {values?.institution?.type === "tertiary" && (
                            <Autocomplete
                              options={UNIVERSITY_YEARS}
                              label="What year are you in at university?"
                              name="secondary_year"
                            />
                          )}
                        </Grid>

                        {/* Teams */}
                        <Grid item xs={12}>
                          <Autocomplete
                            required
                            multiple
                            options={values?.institution?.teams || []}
                            name="teams"
                            label="Select your team(s)"
                            noOptionsText="No teams found"
                          />
                        </Grid>

                        {/* Teacher checkbox - only for secondary/intermediate */}
                        {(values?.institution?.type === "secondary" ||
                          values?.institution?.type === "intermediate") &&
                          !values?.secondary_year?.value && (
                            <Grid item xs={12}>
                              <Checkbox
                                name="teacher_request"
                                label={
                                  <Box display="flex" flexDirection="row" alignItems="center">
                                    <Typography className={classes.icon}>
                                      I am a teacher / facilitator
                                    </Typography>
                                    <Tooltip
                                      open={viewTooltip}
                                      onOpen={handleToogleTooltip}
                                      onClose={handleToogleTooltip}
                                      title="An email with your request will be sent to SVA, we will review your request ASAP. If we will need more information we will contact you. Once approved we will let you know."
                                      placement="top"
                                      classes={{ tooltip: classes.tooltip }}
                                    >
                                      <InfoRounded
                                        fontSize="small"
                                        onClick={handleToogleTooltip}
                                        sx={{ cursor: "pointer" }}
                                      />
                                    </Tooltip>
                                  </Box>
                                }
                              />
                            </Grid>
                          )}
                      </>
                    )}

                    <Grid item xs={12}>
                      <Box display="flex" gap={2} mt={2}>
                        <Button
                          variant="outlined"
                          onClick={handleBack}
                          startIcon={<ArrowBack />}
                          sx={{ flex: 1 }}
                        >
                          Back
                        </Button>
                        <Button
                          variant="contained"
                          onClick={() =>
                            handleNext(values, validateForm, setTouched, setFieldTouched)
                          }
                          endIcon={<ArrowForward />}
                          disabled={!values.institution || values.teams.length === 0}
                          sx={{ flex: 1 }}
                        >
                          Continue
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                )}

                {/* Step 3: Personal Details */}
                {activeStep === 2 && (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography
                        align="center"
                        sx={{ fontSize: 24, fontWeight: 700, color: "primary.main", mb: 1 }}
                      >
                        Personal Details
                      </Typography>
                      <Typography align="center" sx={{ color: "text.secondary", mb: 2 }}>
                        {googleUserData?.isGoogleAuth
                          ? "Complete your profile to start volunteering"
                          : "Create your account to start volunteering"}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        name="first_name"
                        label="First name"
                        disabled={googleUserData?.isGoogleAuth}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        name="last_name"
                        label="Last name"
                        disabled={googleUserData?.isGoogleAuth}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        required
                        name="email"
                        label="Email"
                        autoComplete="username"
                        disabled={googleUserData?.isGoogleAuth}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <PhoneField
                        required
                        name="phone"
                        label="Phone Number"
                        countryOptions={COUNTRIES}
                        setFieldValue={setFieldValue}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DatePicker required label="Date of Birth" name="dob" />
                    </Grid>

                    {/* Password fields - only for manual signup */}
                    {!googleUserData?.isGoogleAuth && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            required
                            name="password"
                            label="Password"
                            type="password"
                            autoComplete="new-password"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            required
                            name="password_confirmation"
                            label="Confirm Password"
                            type="password"
                            autoComplete="new-password"
                          />
                        </Grid>
                      </>
                    )}

                    <Grid item xs={12}>
                      <Checkbox
                        name="subscribe"
                        label="Subscribe to receive our newsletter and updates"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Checkbox
                        name="agree"
                        required
                        label={
                          <Typography>
                            I agree to{" "}
                            <Link
                              target="_blank"
                              rel="noopener"
                              href="https://static1.squarespace.com/static/65dff264c768b913ba2ac4ef/t/662732d4f8221f6fc58dc518/1713844948778/Privacy+Policy+%282%29.pdf"
                            >
                              Privacy Policy
                            </Link>
                          </Typography>
                        }
                      />
                    </Grid>

                    {errors.length > 0 && (
                      <Grid item xs={12}>
                        {errors.map((text, index) => (
                          <Box key={index} mt={1}>
                            <Alert severity="error">{text}</Alert>
                          </Box>
                        ))}
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <Box display="flex" gap={2} mt={2}>
                        <Button
                          variant="outlined"
                          onClick={handleBack}
                          startIcon={<ArrowBack />}
                          sx={{ flex: 1 }}
                        >
                          Back
                        </Button>
                        <LoadingButton
                          loading={isSubmitting}
                          type="submit"
                          variant="contained"
                          sx={{ flex: 1 }}
                        >
                          {googleUserData?.isGoogleAuth ? "Complete Profile" : "Create Account"}
                        </LoadingButton>
                      </Box>
                    </Grid>
                  </Grid>
                )}

                <FormikErrorFocus
                  offset={0}
                  align="top"
                  focusDelay={200}
                  ease="linear"
                  duration={1000}
                />
              </form>
            )}
          </Formik>
        </CardContent>
      </Card>
    </Dialog>
  ) : (
    creatingMemo
  );
}

SignUp.defaultProps = {
  initialGoogleData: null,
};

SignUp.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  initialGoogleData: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    email: PropTypes.string,
    isGoogleAuth: PropTypes.bool,
  }),
};
